import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { LockKey, Scales } from "@phosphor-icons/react";

const PIN_LENGTH = 6;

function PinDots({ value }) {
  return (
    <div className="flex items-center justify-center gap-3 my-6">
      {Array.from({ length: PIN_LENGTH }).map((_, i) => (
        <div
          key={i}
          className={`h-3.5 w-3.5 rounded-full border-2 border-oxblood-600 transition-colors ${
            i < value.length ? "bg-oxblood-600" : "bg-transparent"
          }`}
        />
      ))}
    </div>
  );
}

function Keypad({ onDigit, onBackspace }) {
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
      {keys.map((k, i) => {
        if (k === "") return <div key={i} />;
        if (k === "⌫") {
          return (
            <button
              key={i}
              type="button"
              onClick={onBackspace}
              className="h-16 rounded-xl bg-oxblood-100 hover:bg-oxblood-200 text-oxblood-600 text-lg font-medium transition"
            >
              ⌫
            </button>
          );
        }
        return (
          <button
            key={i}
            type="button"
            onClick={() => onDigit(k)}
            className="h-16 rounded-xl bg-white border border-ivory-border hover:bg-oxblood-50 text-ink-900 text-2xl font-medium transition"
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center heritage-grad p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 text-center">
        <Scales size={40} weight="duotone" className="mx-auto text-oxblood-600 mb-4" />
        <div className="font-serif text-2xl text-ink-900">{title}</div>
        {subtitle && <div className="text-ink-500 text-sm mt-2">{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

export default function PinGate() {
  const { hasPin, setupPin, verifyPin, loginGoogle, logout } = useAuth();

  // ---- first-time setup: enter + confirm ----
  const [stage, setStage] = useState("enter"); // enter | confirm
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  // ---- unlock ----
  const [enteredPin, setEnteredPin] = useState("");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Physical keyboard support (tablet/desktop) without opening a mobile soft
  // keyboard: no <input>, just a global keydown that drives the same handlers as
  // the on-screen keypad. Each branch parks its live handlers in this ref.
  const handlersRef = useRef({ digit: () => {}, backspace: () => {} });
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key >= "0" && e.key <= "9") { e.preventDefault(); handlersRef.current.digit(e.key); }
      else if (e.key === "Backspace") { e.preventDefault(); handlersRef.current.backspace(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (hasPin === null) {
    // Still resolving whether a PIN exists yet.
    return (
      <div className="min-h-screen flex items-center justify-center heritage-grad">
        <div className="font-serif text-2xl text-oxblood-600">Loading…</div>
      </div>
    );
  }

  if (!hasPin) {
    // ---------- FIRST TIME: create a PIN ----------
    const current = stage === "enter" ? pin : confirmPin;
    const setCurrent = stage === "enter" ? setPin : setConfirmPin;

    const handleDigit = (d) => {
      if (current.length >= PIN_LENGTH) return;
      setError("");
      const next = current + d;
      setCurrent(next);
      if (next.length === PIN_LENGTH) {
        if (stage === "enter") {
          setTimeout(() => setStage("confirm"), 150);
        } else {
          if (next !== pin) {
            setError("PINs don't match. Try again.");
            setTimeout(() => {
              setPin("");
              setConfirmPin("");
              setStage("enter");
            }, 700);
            return;
          }
          setBusy(true);
          setupPin(next)
            .catch(() => {
              setError("Could not set PIN. Try again.");
              setPin("");
              setConfirmPin("");
              setStage("enter");
            })
            .finally(() => setBusy(false));
        }
      }
    };
    const handleBackspace = () => setCurrent(current.slice(0, -1));
    handlersRef.current = { digit: handleDigit, backspace: handleBackspace };

    return (
      <Shell
        title={stage === "enter" ? "Set your PIN" : "Confirm your PIN"}
        subtitle={
          stage === "enter"
            ? "Choose a 6-digit PIN. You'll need it every time you open the Case Diary."
            : "Enter the same PIN again."
        }
      >
        <PinDots value={current} />
        {error && <div className="text-sm text-oxblood-600 mb-3">{error}</div>}
        <Keypad onDigit={handleDigit} onBackspace={handleBackspace} />
        {busy && <div className="text-xs text-ink-500 mt-4">Saving…</div>}
        <button
          onClick={logout}
          className="mt-6 text-xs text-ink-500 underline hover:text-ink-700"
        >
          Sign out instead
        </button>
      </Shell>
    );
  }

  // ---------- UNLOCK: enter existing PIN ----------
  const handleDigit = (d) => {
    if (enteredPin.length >= PIN_LENGTH || busy) return;
    setError("");
    const next = enteredPin + d;
    setEnteredPin(next);
    if (next.length === PIN_LENGTH) {
      setBusy(true);
      verifyPin(next)
        .catch((err) => {
          const msg = err?.response?.data?.detail || "Incorrect PIN";
          setError(msg);
          setEnteredPin("");
        })
        .finally(() => setBusy(false));
    }
  };
  const handleBackspace = () => setEnteredPin(enteredPin.slice(0, -1));
  handlersRef.current = { digit: handleDigit, backspace: handleBackspace };

  return (
    <Shell title="Enter your PIN" subtitle="This device is locked. Enter your PIN to continue.">
      <LockKey size={22} className="mx-auto text-oxblood-600/50 mt-1" />
      <PinDots value={enteredPin} />
      {error && <div className="text-sm text-oxblood-600 mb-3">{error}</div>}
      <Keypad onDigit={handleDigit} onBackspace={handleBackspace} />
      {busy && <div className="text-xs text-ink-500 mt-4">Checking…</div>}
      <button
        onClick={logout}
        className="mt-6 text-xs text-ink-500 underline hover:text-ink-700"
      >
        Not you? Sign out
      </button>
    </Shell>
  );
}
